/**
 * <subscription-picker>
 *
 * Wires the custom "Subscribe & Save / One-time" UI to the theme's
 * real add-to-cart form. It lives as a sibling block in the DOM
 * (not inside the form), so on init it locates the nearest
 * add-to-cart form within the same product section and injects a
 * hidden `selling_plan` input that it keeps in sync.
 */
class SubscriptionPicker extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.defaultMode || 'onetime';
    this.setAttribute('data-mode', this.mode);

    this.radios = Array.from(this.querySelectorAll('.subscription-picker__radio'));
    this.freqButtons = Array.from(this.querySelectorAll('.subscription-picker__freq-btn'));
    this.percentText = this.querySelector('[data-ref="percentText"]');

    this.sellingPlanInput = this.#findOrCreateHiddenInput();

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.#setMode(radio.dataset.mode));
    });

    this.freqButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.#selectFrequency(btn));
    });

    // Reflect initial state into the real form.
    this.#setMode(this.mode, { skipDom: true });
    const initiallySelected = this.freqButtons.find((b) => b.classList.contains('is-selected'));
    if (initiallySelected) this.#applyFrequency(initiallySelected);
  }

  #findOrCreateHiddenInput() {
    const sectionId = this.dataset.sectionId;
    const scope =
      (sectionId && document.querySelector(`[data-section-id="${sectionId}"] form[data-type="add-to-cart-form"]`)) ||
      document.querySelector('form[data-type="add-to-cart-form"]');

    if (!scope) {
      console.warn('subscription-picker: could not find the add-to-cart form to attach to.');
      return null;
    }

    let input = scope.querySelector('input[name="selling_plan"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      scope.appendChild(input);
    }
    return input;
  }

  #setMode(mode, { skipDom = false } = {}) {
    this.mode = mode;
    this.setAttribute('data-mode', mode);

    if (this.sellingPlanInput) {
      if (mode === 'subscribe') {
        const selected = this.freqButtons.find((b) => b.classList.contains('is-selected')) || this.freqButtons[0];
        this.sellingPlanInput.value = selected ? selected.dataset.sellingPlanId : '';
      } else {
        this.sellingPlanInput.value = '';
      }
    }

    if (!skipDom) {
      const radioToCheck = this.radios.find((r) => r.dataset.mode === mode);
      if (radioToCheck) radioToCheck.checked = true;
    }
  }

  #selectFrequency(button) {
    this.freqButtons.forEach((b) => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    button.classList.add('is-selected');
    button.setAttribute('aria-pressed', 'true');
    this.#applyFrequency(button);

    if (this.mode === 'subscribe' && this.sellingPlanInput) {
      this.sellingPlanInput.value = button.dataset.sellingPlanId;
    }
  }

  #applyFrequency(button) {
    if (this.percentText) {
      this.percentText.textContent = button.dataset.percent || '0';
    }
  }
}

if (!customElements.get('subscription-picker')) {
  customElements.define('subscription-picker', SubscriptionPicker);
}
